/**
 * Validates if user can proceed to next step
 * Pure function - no side effects, no React dependencies
 *
 * @param {string} stepId - Current step identifier
 * @param {Object} formData - Form field values (from watch() or form data)
 * @param {Object} selections - Selected values and state
 * @param {Object} selections.selectedParticipants - Array of selected participants
 * @param {Object|null} selections.selectedOutcome - Selected outcome object or null
 * @param {string} selections.personInvolvedVisitor - Visitor text input
 * @param {string} selections.personInvolvedVolunteer - Volunteer text input
 * @param {string} selections.personInvolvedOther - Other person text input
 * @param {Array} selections.incidentCategory - Selected incident categories
 * @param {Array} selections.selectedBodyParts - Selected body part IDs
 * @param {string|null} selections.signatureData - Signature data URL
 * @param {Function} selections.getWatchValue - Function to get watch values (for injuries_occurred, etc.)
 * @returns {boolean} True if can proceed, false otherwise
 */
export const validateStep = (stepId, formData, selections) => {
  const {
    selectedParticipants,
    selectedOutcome,
    personInvolvedVisitor,
    personInvolvedVolunteer,
    personInvolvedOther,
    incidentCategory,
    selectedBodyParts,
    signatureData,
    getWatchValue,
  } = selections;

  switch (stepId) {
    case "instructions":
      return true;
    case "basic_details": {
      const hasParticipants = selectedParticipants.length > 0;
      const hasOutcome = selectedOutcome !== null;
      if (selections.timesheetLocked) {
        return hasParticipants && hasOutcome;
      }
      if (!selections.requireTimesheet) {
        return hasParticipants && hasOutcome;
      }
      const hasWorkers = (selections.selectedWorkers || []).length > 0;
      const timesheet = selections.selectedTimesheet;
      const hasTimesheet = !!(timesheet?.uuid || timesheet?.value);
      return hasParticipants && hasOutcome && hasWorkers && hasTimesheet;
    }
    case "medication_form":
      return true;
    case "incident_overview": {
      const hasIncidentCategory = incidentCategory.length > 0;
      const hasInjuriesAnswer =
        getWatchValue("injuries_occurred") === "yes" ||
        getWatchValue("injuries_occurred") === "no";
      const hasPersonResponsibleNotified =
        getWatchValue("person_responsible_notified") === "yes" ||
        getWatchValue("person_responsible_notified") === "no";
      const hasDate = !!(
        formData.incident_overview_date !== undefined &&
        formData.incident_overview_date !== null &&
        formData.incident_overview_date !== ""
      );
      const hasTime = !!(
        formData.incident_overview_time &&
        String(formData.incident_overview_time).trim() !== ""
      );
      return (
        hasIncidentCategory &&
        hasInjuriesAnswer &&
        hasPersonResponsibleNotified &&
        hasDate &&
        hasTime
      );
    }
    case "incident_details":
      return true;
    case "injury_form":
      return selectedBodyParts.length > 0;
    case "attachments":
      return signatureData !== null;
    default:
      return true;
  }
};
